import { Button, Grid } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { alpha } from '@mui/material/styles';
import React, { FC } from 'react';
import { FormProvider } from 'react-hook-form';
import { NewUserType } from 'v2-types/user';
import { DeleteConfirmationModal } from 'components/delete-confirmation';

import SectionItemTitle from './components/container-items';
import GeneralInformationform from './components/gneral-information-form';
import NotificationsForm from './components/notificatiions-form';
import ContactInfoSection from './components/contact-info-section';
import RelationsSection from './components/relations-section';
import AccessSection from './components/access-section';
import PermissionsSection from './components/permissions-section';
import CreateAddressModal from './components/create-address-modal';
import CreateEmails from './components/create-emails-modal';
import CreatePhone from './components/create-phone-modal';
import CreateLicense from './components/create-license-modal';
import CreateAccountModal from './components/create-account-modal';
import CreateAccessModal from './components/create-access-modal';
import PermissionsForm from './components/permissions-form';
import PasswordRecoveryModal from './components/password-recovery-modal';
import { ModalCreateAssociation } from './components/modal-create-association';
import { useContactFormEdit } from './hooks';
import { useQueryClient } from 'react-query';
import SmallAIConsuption from 'components/assistant/components/limits-component';
import { AiUsageModal } from 'components/ai-usage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useContactsVariant from 'views/contacts/hooks/use-contacts-variant';

const useStyles = makeStyles((theme: any) => ({
  resetCountersButton: {
    justifyContent: 'flex-start',
    textTransform: 'none',
    fontWeight: 500,
    padding: theme.spacing(1),
    color: theme.palette.text.primary,
    backgroundColor: alpha(theme.palette.info.main, 0.12),
    '&:hover': { backgroundColor: alpha(theme.palette.info.main, 0.2) },
    '@media (hover: none) and (pointer: coarse)': { minHeight: theme.spacing(5.5) },
  },
  resetCountersIcon: {
    fontSize: 14,
    marginRight: theme.spacing(1.5),
  },
}));

type ContactEditProps = {
  contact: NewUserType;
  refreshContacts: () => void;
  onNameChange?: (name: string) => void;
};

const ContactFormEdit: FC<ContactEditProps> = ({
  contact,
  refreshContacts,
  onNameChange,
}) => {
  const {
    t,
    methods,
    modals,
    setModals,
    deleteModalState,
    dispatchDeleteModal,
    statesByCountry,
    kindList,
    existingMutations,
    existingQuerys,
    existingRoles,
    deleteAddress,
    deleteLicense,
    deleteEmail,
    deletePhone,
    deletePayment,
    deletePartyAssociation,
    updatePartyMutation,
    deleteUser,
    requestPasswordReset,
    isDeletePhoneLoading,
    isDeleteEmailLoading,
    isDeleteLicenseLoading,
    isDeleteWireTransferLoading,
    addresses,
    phones,
    emails,
    licenses,
    payments,
    parents,
    subordinates,
    permissions,
    getOptions,
    resetAICounters,
    isAdmin,
    currentUserEmail,
    isUpdatingParty,
    isDeleteAddressLoading,
    isDeleting,
    isRequestPasswordResetLoading,
    isResettingAICounters
  } = useContactFormEdit(contact, refreshContacts, onNameChange);
  const variant = useContactsVariant();
  const classes = useStyles();
  const queryCache = useQueryClient();

  const email = methods.getValues('username');

  return (
    <FormProvider {...methods}>
      {/* One column on phones, two from sm, the four-column workspace from lg. */}
      <Grid container direction="row" spacing={variant === 'card' ? 2 : 4}>
        {/* Column 1: General Information & Notifications */}
        <Grid item xs={12} sm={6} lg={3}>
          <Grid container direction="column" spacing={3}>
            <Grid item>
              <SectionItemTitle title={t('contact:generalInformationTitle')}>
                <GeneralInformationform
                  contactID={contact._id}
                  updatePartyMutation={updatePartyMutation}
                  kindList={kindList}
                  isTitleCompany={!!contact.kind.includes('Title Company')}
                  isSaving={isUpdatingParty}
                />
              </SectionItemTitle>
            </Grid>
            <Grid item>
              <SectionItemTitle title={t('contact:notificationsTitle')}>
                <NotificationsForm partyID={contact._id} />
              </SectionItemTitle>
            </Grid>
          </Grid>
        </Grid>

        {/* Column 2: Addresses, Phones, Emails, Licenses, Accounts */}
        <Grid item xs={12} sm={6} lg={3}>
          <Grid container direction="column" spacing={3}>
            <ContactInfoSection
              title={t('contact:addressesTitle')}
              fields={addresses.fields}
              onOpenModal={() => setModals('address')}
              isDeleteLoading={isDeleteAddressLoading}
              getItemTitle={(f) => f.kind}
              getItemValue={(f) => f.address}
              dispatchDelete={dispatchDeleteModal}
              getDeleteConfig={(f) => ({
                func: deleteAddress,
                params: { partyId: contact._id, id: f._id },
                message: t('contact:deleteAddressMessage', { address: f.address }),
                title: t('contact:deleteAddressTitle'),
              })}
            />

            <ContactInfoSection
              title={t('contact:phonesTitle')}
              fields={phones.fields}
              onOpenModal={() => setModals('phone')}
              isDeleteLoading={isDeletePhoneLoading}
              getItemTitle={(f) => f.kind}
              getItemValue={(f) => f.number}
              dispatchDelete={dispatchDeleteModal}
              getDeleteConfig={(f) => ({
                func: deletePhone,
                params: { partyId: contact._id, id: f._id },
                message: t('contact:deleteNumberMessage', { number: f.number }),
                title: t('contact:deleteNumberTitle'),
              })}
            />

            <ContactInfoSection
              title={t('contact:emailsTitle')}
              fields={emails.fields}
              onOpenModal={() => setModals('email')}
              isDeleteLoading={isDeleteEmailLoading}
              getItemTitle={(f) => f.kind}
              getItemValue={(f) => f.address}
              disabledDelete={(f) => f.kind === 'Login'}
              dispatchDelete={dispatchDeleteModal}
              getDeleteConfig={(f) => ({
                func: deleteEmail,
                params: { partyId: contact._id, id: f._id },
                message: t('contact:deleteEmailMessage', { email: f.address }),
                title: t('contact:deleteEmailTitle'),
              })}
            />

            <ContactInfoSection
              title={t('contact:licensesTitle')}
              fields={licenses.fields}
              onOpenModal={() => setModals('license')}
              isDeleteLoading={isDeleteLicenseLoading}
              getItemTitle={(f) => statesByCountry.find((s) => s.code === f.state)?.name || ''}
              getItemValue={(f) => f.number}
              dispatchDelete={dispatchDeleteModal}
              getDeleteConfig={(f) => ({
                func: deleteLicense,
                params: { partyId: contact._id, id: f._id },
                message: t('contact:deleteLicenseMessage', { license: f.number }),
                title: t('contact:deleteLicenseTitle'),
              })}
            />

            <ContactInfoSection
              title={t('contact:accountsTitle')}
              fields={payments.fields}
              onOpenModal={() => setModals('account')}
              isDeleteLoading={isDeleteWireTransferLoading}
              getItemTitle={(f) => f.bank}
              getItemValue={(f) => f.account}
              dispatchDelete={dispatchDeleteModal}
              getDeleteConfig={(f) => ({
                func: deletePayment,
                params: { partyId: contact._id, paymentId: f._id },
                message: t('contact:deleteAccountMessage', { account: f.account, bank: f.bank }),
                title: t('contact:deleteAccountTitle'),
              })}
            />
          </Grid>
        </Grid>

        {/* Column 3: Relations */}
        <Grid item xs={12} sm={6} lg={3}>
          <Grid container direction="column" spacing={3}>
            <RelationsSection
              contactId={contact._id}
              parents={parents.fields}
              subordinates={subordinates.fields}
              onOpenModal={() => setModals('relations')}
              deletePartyAssociation={deletePartyAssociation}
              dispatchDelete={dispatchDeleteModal}
            />
          </Grid>
        </Grid>

        {/* Column 4: Access, AI Usage, Permissions */}
        <Grid item xs={12} sm={6} lg={3}>
          <Grid container direction="column" spacing={3}>
            <AccessSection
              contactId={contact._id}
              username={methods.getValues('username')}
              onOpenAccessModal={() => setModals('access')}
              onOpenRecoveryModal={() => setModals('recovery')}
              deleteUser={deleteUser}
              dispatchDelete={dispatchDeleteModal}
            />

            {isAdmin && !!email && currentUserEmail !== email &&
              <Grid item>
                <SectionItemTitle
                  title={t('contact:ai-usage-title')}
                  icon="info-circle"
                  iconLabel={t('contact:ai-usage-details')}
                  onClickIcon={() => setModals('ai-usage')}
                >
                  <SmallAIConsuption
                    email={email}
                    refetchOnUsageUpdate={false}
                  />
                  <Button
                    fullWidth
                    disabled={isResettingAICounters}
                    className={classes.resetCountersButton}
                    onClick={() => { resetAICounters({ email }); }}
                  >
                    <FontAwesomeIcon
                      icon={isResettingAICounters ? 'spinner' : 'undo'}
                      spin={isResettingAICounters}
                      className={classes.resetCountersIcon}
                    />
                    {t('contact:reset-ai-counters')}
                  </Button>
                </SectionItemTitle>
              </Grid>}

            {methods.getValues('username') !== null && (
              <PermissionsSection
                permissions={permissions.fields}
                onOpenModal={() => setModals('permissions')}
                getOptions={getOptions}
              />
            )}
          </Grid>
        </Grid>

        {/* Modals */}
        {modals === 'address' && (
          <CreateAddressModal
            open={modals === 'address'}
            onClose={() => setModals(null)}
            partyId={contact._id}
            onSuccess={(localaddresses) => {
              methods.setValue('addresses', localaddresses);
            }}
            states={statesByCountry}
          />
        )}
        {modals === 'phone' && (
          <CreatePhone
            open={modals === 'phone'}
            onClose={() => setModals(null)}
            partyId={contact._id}
            onSuccess={(localphones) => {
              methods.setValue('phones', localphones);
            }}
          />
        )}
        {modals === 'email' && (
          <CreateEmails
            open={modals === 'email'}
            onClose={() => setModals(null)}
            partyId={contact._id}
            onSuccess={(localemails) => {
              methods.setValue('emails', localemails);
            }}
          />
        )}
        {modals === 'license' && (
          <CreateLicense
            open={modals === 'license'}
            onClose={() => setModals(null)}
            partyId={contact._id}
            onSuccess={(locallicenses) => {
              methods.setValue('licenses', locallicenses);
            }}
            states={statesByCountry}
          />
        )}
        {modals === 'relations' && (
          <ModalCreateAssociation
            partyId={contact._id}
            isTitleCompany={contact.kind.includes('Title Company')}
            isTitleAgency={contact.kind.includes('Title Agency')}
            open={modals === 'relations'}
            onSuccess={() => {
              queryCache.invalidateQueries('getCurrentUser');
              refreshContacts();
            }}
            onClose={() => {
              setModals(null);
            }}
            onError={() => { }}
          />
        )}
        {modals === 'access' && (
          <CreateAccessModal
            partyId={contact._id}
            open={modals === 'access'}
            onSuccess={() => refreshContacts()}
            onClose={() => setModals(null)}
          />
        )}
        {modals === 'permissions' && (
          <PermissionsForm
            open={modals === 'permissions'}
            permissions={permissions?.fields || []}
            onClose={() => setModals(null)}
            partyId={contact._id}
            existingMutations={existingMutations}
            existingQuerys={existingQuerys}
            existingRoles={existingRoles}
            onSuccess={(permissionsRes) => {
              methods.setValue('permissions', permissionsRes);
            }}
          />
        )}
        {modals === 'account' && (
          <CreateAccountModal
            open={modals === 'account'}
            partyId={contact._id}
            onClose={() => setModals(null)}
            onSuccess={() => { }}
          />
        )}
        {deleteModalState.mode === 'delete' && (
          <DeleteConfirmationModal
            isOpen
            title={deleteModalState.title}
            confirmationMessage={deleteModalState.message}
            onClose={() => dispatchDeleteModal({ type: null })}
            onConfirm={() => {
              const { func, params } = deleteModalState.data!;
              if (func) {
                func({ ...params });
              }
            }}
            isRemoveLoading={isDeleting}
            buttonConfirmText={t('common:delete')}
          />
        )}
        {modals === 'recovery' && (
          <PasswordRecoveryModal
            open={modals === 'recovery'}
            email={email}
            isSending={isRequestPasswordResetLoading}
            onClose={() => setModals(null)}
            onSend={() => requestPasswordReset({ email })}
          />
        )}
        {modals === "ai-usage" && !!email && (
          <AiUsageModal
            email={email}
            onClose={() => setModals(null)}
          />
        )}
      </Grid>
    </FormProvider>
  );
};

export default ContactFormEdit;